import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {onClickRemoveVideoBG, onOpenTrimVideoModal, onOpenVideoCropModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  showCopyStylesAsNewFeature,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {openPosterEditorItemFlipModal} from '@Modals/poster-editor-item-flip-modal';
import {openPosterEditorItemStretchModal} from '@Modals/poster-editor-item-stretch-modal';
import {openPosterEditorHyperlinkModal} from '@Modals/poster-editor-item-hyperlink-modal';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal';
import {openPosterEditorItemFiltersModal} from '@Modals/poster-editor-item-filters-modal';
import {openPosterEditorItemAdjustmentsModal} from '@Modals/poster-editor-item-adjustments-modal';
import {openPosterEditorItemEffectsModal} from '@Modals/poster-editor-item-effects-modal';
import {openReplaceMediaModal} from '@Modals/replace-media-modal';
import {useEditorPrefetchMediaMobileModules} from '@Hooks/poster-editor/useEditorPrefetchMediaMobileModules';
import {RemoveVideoBackgroundNotificationTag} from '@Components/poster-editor/components/remove-video-background-notification-tag';
import {VideoItemBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import type {BottomBarItemProps} from '@Components/contextual-menu/components/bottom-bar-item';
import {getAiTranscriptParamsForVideo, getSelectableItemDataForVideoData, showAiSubtitlesUpsellDialog} from '@Libraries/ai-transcript.library';
import {AiSubtitleState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {useAiTranscriptGeneration} from '@Hooks/poster-editor/useAiTranscriptGeneration';
import {openAiSubtitleGenerationLanguageSelectorModal} from '@Modals/ai-subtitle-generation-language-selector-modal/ai-subtitle-generation-language-selector-modal.types';
import {clearGeneratedResults, setAiSubtitlesPanelItems} from '@Panels/ai-subtitle-panel/ai-subtitle-reducer';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {AiTranscriptAction} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './video-bottom-bar.module.scss';
import {getCopiedStyles, getCurrentPageDuration} from '@Components/poster-editor/poster-editor-reducer';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {GA4EventName, GA4EventParam, GA4EventParamName, GA4EventSources, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {useGetKeyFromActiveVideoItem} from '@Hooks/poster-editor/useGetKeyFromActiveVideoItem';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';
import {openPosterEditorItemVolumeModal} from '@Modals/poster-editor-item-volume-modal/poster-editor-item-volume-modal';
import {openPosterEditorItemSpeedModal} from '@Modals/poster-editor-item-speed-modal/poster-editor-item-speed-modal';
import {useGetTeamCredits} from '@Hooks/useGetTeamCredits';
import {FreeBgrMobileNotification} from '@Components/free-bgr-mobile-notification';
import {VIDEO_BGR_PROMO_MAX_DURATION_SECONDS} from '@Libraries/user.library';

export function VideoBottomBar(): ReactElement | null {
  const dispatch = useAppDispatch();
  const isLocked = useGetKeyFromActiveItem('lockMovementX');
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());

  const currentPageDuration = useAppSelector(getCurrentPageDuration);
  const endTime = useGetKeyFromActiveVideoItem('endTime');
  const startTime = useGetKeyFromActiveVideoItem('startTime');

  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const credits = useGetTeamCredits() ?? 0;
  const videoBGRPromo = useAppSelector((state) => state.teamCredits.videoBGRPromo);
  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;
  useEditorPrefetchMediaMobileModules();
  const isBackgroundBeingRemoved = useAppSelector((state) => {
    const item = state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsHashMap[activeItemId ?? ''];
    if (item && 'removeBackground' in item) {
      return item.removeBackground.isBackgroundBeingRemoved;
    }
    return false;
  });
  const isBackgroundRemoved = useAppSelector((state) => {
    const item = state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsHashMap[activeItemId ?? ''];
    if (item && 'removeBackground' in item) {
      return item.removeBackground.isBackgroundRemoved;
    }
    return false;
  });

  const aiSubtitleState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiSubtitles;
  });

  const {getAiSubtitlesActionForItem, addAiTranscripts} = useAiTranscriptGeneration();

  if (!activeItem || (!activeItem.isVideo() && !activeItem.isVideoSlide())) {
    return null;
  }

  const onClickRemoveBG = async (): Promise<void> => {
    await onClickRemoveVideoBG(activeItem, credits);
    trackPosterBuilderGA4Events(GA4EventName.REMOVE_BACKGROUND_VIDEO, {[GA4EventParamName.SOURCE]: GA4EventSources.MOBILE_BOTTOM_BAR});
  };

  const areAiSubtitlesDisabled = (): boolean => {
    if (!currentPageDuration || !endTime || startTime === undefined) {
      return true;
    }
    const loopCycles = currentPageDuration / ((endTime - startTime) / activeItem.speed);

    return (
      getAiSubtitlesActionForItem(getAiTranscriptParamsForVideo(activeItem.toObject(), loopCycles)) === AiTranscriptAction.GENERATED ||
      aiSubtitleState === AiSubtitleState.GENERATING
    );
  };

  const pulsatingDotForCopyStyles = (): ReactElement | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }

    return null;
  };

  const onGenerateAiSubtitles = async (): Promise<void> => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }

    const itemIdsInOrder = currentPage.items.getItemIdsInOrder();
    const videoItemIdsInOrder = itemIdsInOrder.filter((itemId) => {
      const item = currentPage.items.itemsHashMap[itemId];
      return item.isVideo();
    });
    const orderOfCurrentVideoItem = videoItemIdsInOrder.indexOf(activeItem.uid) + 1;

    if (!currentPageDuration || !endTime || startTime === undefined) {
      return;
    }
    const loopCycles = currentPageDuration / ((endTime - startTime) / activeItem.speed);

    dispatch(
      setAiSubtitlesPanelItems([
        {
          ...getSelectableItemDataForVideoData(activeItem.toObject(), loopCycles, orderOfCurrentVideoItem),
          isSelected: true,
        },
      ])
    );

    dispatch(clearGeneratedResults());

    dispatch(closeModal(MODALS_IDS.AI_SUBTITLE_GENERATION_LANGUAGE_SELECTOR_MODAL));

    await addAiTranscripts([getAiTranscriptParamsForVideo(activeItem.toObject(), loopCycles)]);
  };

  const onClickHandler = (itemId: VideoItemBottomBarOptions): void => {
    switch (itemId) {
      case VideoItemBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case VideoItemBottomBarOptions.CROP:
        onOpenVideoCropModal(activeItem, true);
        break;
      case VideoItemBottomBarOptions.TRIM:
        onOpenTrimVideoModal(activeItem);
        break;
      case VideoItemBottomBarOptions.REMOVE_BG:
        void onClickRemoveBG();
        break;
      case VideoItemBottomBarOptions.UNMUTE:
        activeItem.toggleMuteUnmuteState();
        break;
      case VideoItemBottomBarOptions.VOLUME:
        if (!activeItem.hasAudio) {
          return;
        }
        openPosterEditorItemVolumeModal();
        break;
      case VideoItemBottomBarOptions.SPEED:
        openPosterEditorItemSpeedModal();
        break;
      case VideoItemBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case VideoItemBottomBarOptions.EFFECTS:
        openPosterEditorItemEffectsModal();
        break;
      case VideoItemBottomBarOptions.FILTER:
        openPosterEditorItemFiltersModal();
        break;
      case VideoItemBottomBarOptions.ADJUSTMENTS:
        openPosterEditorItemAdjustmentsModal();
        break;
      case VideoItemBottomBarOptions.REPLACE:
        openReplaceMediaModal();
        break;
      case VideoItemBottomBarOptions.FLIP:
        openPosterEditorItemFlipModal();
        break;
      case VideoItemBottomBarOptions.STRETCH:
        openPosterEditorItemStretchModal();
        break;
      case VideoItemBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case VideoItemBottomBarOptions.OPACITY:
        openPosterEditorItemOpacityModal();
        break;
      case VideoItemBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      case VideoItemBottomBarOptions.HYPERLINK:
        openPosterEditorHyperlinkModal();
        break;
      case VideoItemBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case VideoItemBottomBarOptions.DELETE:
        deleteItemByID(activeItem.uid);
        break;
      case VideoItemBottomBarOptions.AI_SUBTITLES:
        if (areAiSubtitlesDisabled()) {
          return;
        }

        if (!isPremium.current) {
          void showAiSubtitlesUpsellDialog();
          return;
        }

        trackPosterBuilderGA4Events(GA4EventName.AI_SUBTITLE_CLICK_CONTEXTUAL_MENU, {[GA4EventParamName.TYPE]: GA4EventParam.VIDEO});
        openAiSubtitleGenerationLanguageSelectorModal({onGenerateAiSubtitles});
        break;
      default:
        throw new Error(`Unhandled itemId ${itemId}`);
    }
  };

  const getTagForAiSubtitleOption = (): ReactElement | undefined => {
    if (!isPremium.current) {
      return <Icon icon="icon-crown" size={IconSize.SIZE_ICON_20} className="color-premium" />;
    }

    return undefined;
  };

  const getAiSubtitlesOption = (): BottomBarItemProps => {
    if (!currentPageDuration || !endTime || startTime === undefined) {
      throw new Error('unable to get endtime, start time or current page duration');
    }
    const loopCycles = currentPageDuration / ((endTime - startTime) / activeItem.speed);

    if (getAiSubtitlesActionForItem(getAiTranscriptParamsForVideo(activeItem.toObject(), loopCycles)) === AiTranscriptAction.GENERATED) {
      return {
        id: VideoItemBottomBarOptions.AI_SUBTITLES,
        isAiOption: true,
        icon: 'icon-check',
        text: window.i18next.t('pmwjs_ai_subtitles'),
        isDisabled: true,
        tag: getTagForAiSubtitleOption(),
      };
    }

    return {
      id: VideoItemBottomBarOptions.AI_SUBTITLES,
      isAiOption: true,
      icon: 'icon-ai-subtitles',
      text: window.i18next.t('pmwjs_ai_subtitles'),
      isLoading: aiSubtitleState === AiSubtitleState.GENERATING,
      isTextBold: true,
      loadingText: window.i18next.t('pmwjs_generating'),
      tag: getTagForAiSubtitleOption(),
    };
  };

  const getCustomNotificationForBGR = (): ReactElement | undefined => {
    if (
      videoBGRPromo?.isEligible &&
      !isBackgroundBeingRemoved &&
      !isBackgroundRemoved &&
      endTime &&
      startTime !== undefined &&
      endTime - startTime <= VIDEO_BGR_PROMO_MAX_DURATION_SECONDS
    ) {
      return <FreeBgrMobileNotification />;
    }
    return undefined;
  };

  return (
    <BottomBar
      id="poster-editing-default-bottom-bar"
      onClick={onClickHandler}
      items={[
        {
          id: VideoItemBottomBarOptions.CROP,
          icon: 'icon-crop',
          text: window.i18next.t('pmwjs_crop'),
        },
        {
          id: VideoItemBottomBarOptions.TRIM,
          icon: 'icon-cut',
          text: window.i18next.t('pmwjs_trim'),
        },
        {...getAiSubtitlesOption()},
        {
          id: VideoItemBottomBarOptions.REMOVE_BG,
          icon: 'icon-remove-bg',
          text: window.i18next.t('pmwjs_ai_bg_remover'),
          textClasses: `spacing-p-0`,
          isLoading: isBackgroundBeingRemoved,
          isTextBold: true,
          loadingText: window.i18next.t('pmwjs_removing_background'),
          tag: (
            <RemoveVideoBackgroundNotificationTag
              videoHashedFilename={activeItem.hashedFilename}
              startTime={activeItem.getStartTimeForNonRemovedBgVideo()}
              endTime={activeItem.getEndTimeForNonRemovedBgVideo()}
            />
          ),
          isSelected: isBackgroundRemoved && !isBackgroundBeingRemoved,
          customNotification: getCustomNotificationForBGR(),
        },
        {
          id: VideoItemBottomBarOptions.UNMUTE,
          icon: !activeItem.hasAudio || !activeItem.isMuted() ? 'icon-sound' : 'icon-sound-off',
          text: activeItem.isMuted() ? window.i18next.t('pmwjs_unmute') : window.i18next.t('pmwjs_mute'),
          isDisabled: !activeItem.hasAudio,
          isHidden: true,
        },
        {
          id: VideoItemBottomBarOptions.VOLUME,
          icon: 'icon-sound',
          text: window.i18next.t('pmwjs_volume'),
          isDisabled: !activeItem.hasAudio,
        },
        {
          id: VideoItemBottomBarOptions.SPEED,
          icon: 'icon-video-in-page',
          text: window.i18next.t('pmwjs_speed'),
        },
        {
          id: VideoItemBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: VideoItemBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: VideoItemBottomBarOptions.EFFECTS,
          icon: 'icon-effects',
          text: window.i18next.t('pmwjs_effects'),
        },
        {
          id: VideoItemBottomBarOptions.FILTER,
          icon: 'icon-filters',
          text: window.i18next.t('pmwjs_filters'),
        },
        {
          id: VideoItemBottomBarOptions.ADJUSTMENTS,
          icon: 'icon-filter-1',
          text: window.i18next.t('pmwjs_adjustments'),
        },
        {
          id: VideoItemBottomBarOptions.REPLACE,
          icon: 'icon-replace',
          text: window.i18next.t('pmwjs_replace'),
        },
        {
          id: VideoItemBottomBarOptions.FLIP,
          icon: 'icon-flip-horizontal',
          text: window.i18next.t('pmwjs_flip'),
        },
        {
          id: VideoItemBottomBarOptions.STRETCH,
          icon: 'icon-fit-to-width',
          text: window.i18next.t('pmwjs_stretch'),
        },
        {
          id: VideoItemBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: VideoItemBottomBarOptions.OPACITY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: VideoItemBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: VideoItemBottomBarOptions.HYPERLINK,
          icon: 'icon-link',
          text: window.i18next.t('pmwjs_hyperlink'),
        },
        {
          id: VideoItemBottomBarOptions.LOCK,
          icon: isLocked ? 'icon-lock' : 'icon-unlock',
          text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: VideoItemBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
    />
  );
}
